import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faCheckCircle,
  faExclamationCircle,
  faFileExcel,
  faPlus,
  faTrash,
} from '@fortawesome/sharp-regular-svg-icons'
import {
  ActionIcon,
  Anchor,
  Button,
  Grid,
  Group,
  Modal,
  Text,
} from '@mantine/core'
import { Dropzone, type FileWithPath } from '@mantine/dropzone'
import { useDisclosure } from '@mantine/hooks'
import { showNotification } from '@mantine/notifications'
import { useQueryClient } from '@tanstack/react-query'
import React, { useRef, useState } from 'react'

import { ORDER_JOBS_QUERY_KEY } from '..'
import { useUploadLabelXls } from '../upload'

const UploadButton = (): React.ReactElement => {
  const uploadLabelXls = useUploadLabelXls()
  const openRef = useRef<() => void>(null)
  const [file, setFile] = useState<FileWithPath[]>([])
  const [opened, { open, close }] = useDisclosure()
  const queryClient = useQueryClient()

  const closeHandler = (): void => {
    setFile([])
    close()
  }

  const onSubmit = (): void => {
    uploadLabelXls.mutate(
      { assets: file },
      {
        onSuccess: () => {
          showNotification({
            color: 'green',
            title: i18n.t('noti_title_success', `Erfolg!`),
            message: i18n.t(
              'success_order_job_upload',
              'Aufträgte wurden importiert. Labels stehen in Kürze zum Download bereit.',
            ),
            icon: <FontAwesomeIcon icon={faCheckCircle} />,
          })
          queryClient.invalidateQueries({ queryKey: ORDER_JOBS_QUERY_KEY })
          closeHandler()
        },
        onError: () => {
          showNotification({
            color: 'red',
            title: i18n.t('error_sorry_title', 'Entschuldigung!'),
            message: i18n.t(
              'error_order_job_upload',
              'Aufträge konnte nicht importiert werden.',
            ),
            icon: <FontAwesomeIcon icon={faExclamationCircle} />,
          })
        },
      },
    )
  }

  return (
    <Group position="right">
      <Modal
        size="lg"
        title={i18n.t('order_upload_modal_title', 'Aufträge importieren')}
        opened={opened}
        onClose={close}
        withCloseButton={!uploadLabelXls.isPending}
      >
        <Grid>
          <Grid.Col>
            <Dropzone
              style={{ display: 'none' }}
              maxFiles={1}
              onDrop={(f) => {
                console.log({ f })
                setFile(f)
              }}
              openRef={openRef}
              accept={[
                'application/vnd.ms-excel',
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
              ]}
            >
              <></>
            </Dropzone>
            {file?.length === 0 ? (
              <>
                <Group position="center">
                  <Button
                    leftIcon={<FontAwesomeIcon icon={faFileExcel} />}
                    variant="light"
                    fullWidth
                    onClick={() => openRef.current?.()}
                  >
                    {i18n.t('auftrag_file_select_', 'Auftragsdatei auswählen')}
                  </Button>
                </Group>
                <Group position="right" py="xs">
                  <Anchor
                    target="_blank"
                    href="" // TODO LEON AU-162: Add example file link
                  >
                    {i18n.t('example_file_dl_anchor', 'Beispieldatei')}
                  </Anchor>
                </Group>
              </>
            ) : (
              <Group position="apart">
                <Text size="lg">{file[0]?.name}</Text>
                <ActionIcon
                  onClick={() => setFile([])}
                  color="red"
                  disabled={uploadLabelXls.isPending}
                >
                  <FontAwesomeIcon icon={faTrash} />
                </ActionIcon>
              </Group>
            )}
          </Grid.Col>

          <Grid.Col>
            <Group position="right" mt="lg">
              <Button
                variant="light"
                type="submit"
                onClick={onSubmit}
                loading={uploadLabelXls.isPending}
                disabled={file.length === 0}
              >
                {i18n.t('upload_mass_order_btn', 'Aufträge importieren')}
              </Button>
            </Group>
          </Grid.Col>
        </Grid>
      </Modal>
      <Button
        size="xs"
        variant="light"
        leftIcon={<FontAwesomeIcon icon={faPlus} />}
        onClick={open}
      >
        {i18n.t('upload_mass_order_btn', 'Aufträge importieren')}
      </Button>
    </Group>
  )
}

export default UploadButton
